import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import MobileLoadingSkeleton from './MobileLoadingSkeleton';
import MobileEmptyState from './MobileEmptyState';
import MobileSearchBar from './MobileSearchBar';

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const MobileVenue = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        let venuesData = [];
        try {
          const { data, error } = await supabase.from('venues').select('*').order('name');
          if (!error) venuesData = data || [];
        } catch (e) {}

        const { data: eventsData } = await supabase
          .from('events')
          .select('id, title, venue_id, venue_name, start_date, end_date, status, location')
          .in('status', ['upcoming', 'ongoing'])
          .order('start_date');

        const venuesWithEvents = venuesData.map(venue => {
          const venueEvents = (eventsData || []).filter(e =>
            e.venue_id === venue.id || e.venue_name?.toLowerCase() === venue.name?.toLowerCase()
          );
          return { ...venue, events: venueEvents };
        });

        const eventVenueNames = new Set(venuesData.map(v => v.name?.toLowerCase()));
        const additionalVenues = [];
        (eventsData || []).forEach(event => {
          const venueName = event.venue_name || '';
          if (venueName && !eventVenueNames.has(venueName.toLowerCase())) {
            const existing = additionalVenues.find(v => v.name?.toLowerCase() === venueName.toLowerCase());
            if (existing) {
              existing.events.push(event);
            } else {
              const locationParts = event.location?.split(',') || [];
              additionalVenues.push({
                id: `temp-${venueName}`,
                name: venueName,
                city: locationParts[0]?.trim() || '',
                state: locationParts[1]?.trim() || '',
                events: [event],
              });
            }
            eventVenueNames.add(venueName.toLowerCase());
          }
        });

        setVenues([...venuesWithEvents, ...additionalVenues]);
      } catch (error) {
        console.error('Error fetching venues:', error);
        setVenues([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  const filteredVenues = useMemo(() => {
    if (!searchTerm) return venues;
    const term = searchTerm.toLowerCase();
    return venues.filter(v =>
      v.name?.toLowerCase().includes(term) ||
      v.city?.toLowerCase().includes(term) ||
      v.state?.toLowerCase().includes(term)
    );
  }, [venues, searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div style={{ height: 'env(safe-area-inset-top)' }} />
        <div className="px-5 pt-6">
          <MobileLoadingSkeleton count={1} height={120} rounded="rounded-3xl" />
          <div className="mt-4 space-y-3">
            <MobileLoadingSkeleton count={3} height={100} rounded="rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
      <div style={{ height: 'env(safe-area-inset-top)' }} />

      {/* Gradient header */}
      <div className="bg-gradient-to-br from-primary-dark to-primary px-5 pt-6 pb-8 rounded-b-3xl">
        <Link to="/" className="text-white/70 text-sm inline-flex items-center mb-3">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>
        <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center mb-3 ring-1 ring-white/20">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h1 className="font-display text-2xl font-bold text-white">Venues</h1>
        <p className="text-white/60 text-sm mt-1">Conference locations and event spaces</p>
      </div>

      {/* Search */}
      <div className="px-5 mt-4">
        <MobileSearchBar
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search venues by name or city..."
        />
      </div>

      {/* Venue list */}
      <div className="px-5 mt-4">
        {filteredVenues.length === 0 ? (
          <MobileEmptyState
            icon={<svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
            title="No venues found"
            message={searchTerm ? "Try a different search term" : "Venues will appear here once events are listed."}
          />
        ) : (
          <div className="space-y-3">
            {filteredVenues.map((venue) => (
              <div key={venue.id} className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{venue.name}</h3>
                    {(venue.city || venue.state) && (
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">
                        {[venue.city, venue.state].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {venue.events?.length > 0 && (
                      <div className="mt-2.5 space-y-1.5">
                        {venue.events.slice(0, 3).map((event) => (
                          <Link
                            key={event.id}
                            to={`/event/${event.id}`}
                            className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 active:text-primary"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                            <span className="truncate">{event.title}</span>
                            <span className="text-gray-300 flex-shrink-0">{formatDate(event.start_date)}</span>
                          </Link>
                        ))}
                        {venue.events.length > 3 && (
                          <p className="text-xs text-gray-400 pl-3.5">+{venue.events.length - 3} more events</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileVenue;
